import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, PenTool, AlertTriangle } from 'lucide-react';
import { api } from '../services/api';
import { Profile } from '../../types';
import { fetchSignatariosDefault, SignatariosEscolhidos, SignatarioTermo } from '../lib/termoSignatarios';

interface SignatariosSelectorProps {
    /** Exibe o seletor de procurador/representante legal (default: true). */
    comProcurador?: boolean;
    /** Exibe os seletores das 2 testemunhas (default: false). */
    comTestemunhas?: boolean;
    value: SignatariosEscolhidos;
    onChange: (v: SignatariosEscolhidos) => void;
    /** Rótulo do papel do procurador no documento (default: "Representante Legal"). */
    labelProcurador?: string;
}

const selectCls = "w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100";
const labelCls = "block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider";

export const signatariosVazio: SignatariosEscolhidos = { procurador: null, testemunha1: null, testemunha2: null };

/**
 * Seletor de signatários internos (representante legal + testemunhas) usado nos
 * modais de documentos (advertência, notificação, aditivo, distrato). Vem
 * pré-selecionado com o padrão de Configurações → Assinaturas do Termo, mas o
 * operador pode trocar por documento.
 */
const SignatariosSelector: React.FC<SignatariosSelectorProps> = ({
    comProcurador = true,
    comTestemunhas = false,
    value,
    onChange,
    labelProcurador = 'Representante Legal'
}) => {
    const [users, setUsers] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const defaultsAplicados = useRef(false);
    // onChange muda de identidade a cada render do pai — ref evita re-disparo do efeito de carga
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const valueRef = useRef(value);
    valueRef.current = value;

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [profiles, defaults] = await Promise.all([
                    api.auth.listProfiles(),
                    fetchSignatariosDefault()
                ]);
                if (!active) return;
                setUsers(profiles.filter(u => u.status !== 'inactive'));
                if (!defaultsAplicados.current) {
                    defaultsAplicados.current = true;
                    const atual = valueRef.current;
                    onChangeRef.current({
                        procurador: atual.procurador ?? defaults.procurador,
                        testemunha1: atual.testemunha1 ?? defaults.testemunha1,
                        testemunha2: atual.testemunha2 ?? defaults.testemunha2
                    });
                }
            } catch (err) {
                console.error('Erro ao carregar signatários:', err);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, []);

    // Elegibilidade: e-mail corporativo sempre; CPF porque os documentos imprimem o CPF do signatário
    const elegiveis = useMemo(
        () => users.filter(u => !!(u.email_corporativo?.trim() && u.cpf?.trim())),
        [users]
    );

    const toSignatario = (u: Profile): SignatarioTermo => ({
        nome: u.full_name || '',
        cpf: u.cpf || '',
        email: u.email_corporativo || ''
    });

    const renderSelect = (
        label: string,
        selecionado: SignatarioTermo | null,
        set: (s: SignatarioTermo | null) => void,
        excluirEmail?: string | null
    ) => (
        <div>
            <label className={labelCls}>{label} *</label>
            <select
                className={selectCls}
                value={selecionado ? selecionado.email : ''}
                onChange={e => {
                    const u = elegiveis.find(x => x.email_corporativo === e.target.value);
                    set(u ? toSignatario(u) : null);
                }}
            >
                <option value="">Selecione...</option>
                {elegiveis.map(u => (
                    <option
                        key={u.id}
                        value={u.email_corporativo || ''}
                        disabled={!!excluirEmail && u.email_corporativo === excluirEmail}
                    >
                        {u.full_name}{u.departamento ? ` — ${u.departamento}` : ''}
                    </option>
                ))}
            </select>
            {selecionado && (
                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1 truncate">
                    CPF: {selecionado.cpf || '—'} · {selecionado.email}
                </p>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-slate-800/60 border border-gray-100 dark:border-slate-700/50 rounded-xl text-sm text-gray-500 dark:text-slate-400">
                <Loader2 size={14} className="animate-spin" /> Carregando signatários...
            </div>
        );
    }

    const faltando =
        (comProcurador && !value.procurador) ||
        (comTestemunhas && (!value.testemunha1 || !value.testemunha2));

    return (
        <div className="space-y-3">
            <h3 className="text-sm font-bold text-gray-800 dark:text-slate-200 border-b border-gray-100 dark:border-slate-700 pb-2 flex items-center gap-2">
                <PenTool size={16} className="text-primary" /> Signatários Internos
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {comProcurador && renderSelect(labelProcurador, value.procurador, s => onChange({ ...value, procurador: s }))}
                {comTestemunhas && renderSelect('Testemunha 1', value.testemunha1, s => onChange({ ...value, testemunha1: s }), value.testemunha2?.email)}
                {comTestemunhas && renderSelect('Testemunha 2', value.testemunha2, s => onChange({ ...value, testemunha2: s }), value.testemunha1?.email)}
            </div>
            {faltando && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900 rounded-xl">
                    <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                        Defina o padrão em <strong>Configurações → Assinaturas do Termo</strong>, ou selecione acima.
                        Só aparecem usuários com e-mail corporativo e CPF cadastrados.
                    </p>
                </div>
            )}
        </div>
    );
};

export default SignatariosSelector;
